import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import UseAxios from '../../../Api/http/http';
import {
  createProjectProgress,
  getProjectStatusMaster,
  updateProjectProgress,
} from '../../../Api/request/projectDetails';
import {
  createStatusChangeRequest,
  listStatusChangeRequestHistory,
} from '../../../Api/request/statusChangeRequest';
import { createTimelineRequest } from '../../../Api/request/timelineChangeRequest';
import { useAuth } from '../../../Contexts/AuthContext';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';

const DEFAULT_HEADER_MODALS = {
  statusEdit: { isOpen: false, data: null },
  timelineEdit: { isOpen: false, data: null },
  progressSummaryEdit: { isOpen: false, data: null },
};
const OPEN_REQUEST_BLOCK_MESSAGE = 'A new request can not be raised as one request is already open.';
const TIMELINE_FIELD_DISPLAY_MAP = {
  rlsd: 'Baseline Start Date',
  rled: 'Baseline End Date',
  acsd: 'Actual Start Date',
  rped: 'Replanned End Date',
};

// eslint-disable-next-line max-lines-per-function
const useHeaderModals = (projectData, { projectSeqId = null, onRefreshData = null, canEditTimeline = false } = {}) => {
  const navigate = useNavigate();
  const [headerModals, setHeaderModals] = useState(DEFAULT_HEADER_MODALS);
  const [submitting, setSubmitting] = useState({
    statusEdit: false,
    timelineEdit: false,
    progressSummaryEdit: false,
  });
  const [statusOptions, setStatusOptions] = useState([]);
  const statusEditRequestIdRef = useRef(0);
  const { userProfile } = useAuth();
  const { snackbarOpen } = useSnackbar();
  const accessToken = useMemo(
    () => userProfile?.access_token || '',
    [userProfile],
  );
  const employeeId = useMemo(
    () => userProfile?.employeeId || userProfile?.employee_id || '',
    [userProfile],
  );
  const employeeNo = useMemo(
    () => userProfile?.employeeNo || userProfile?.emp_no || userProfile?.employee_no || '',
    [userProfile],
  );
  const projectId = useMemo(
    () => projectData?.project_id || projectData?.projectId || '',
    [projectData],
  );
  const projectName = useMemo(
    () => projectData?.project_name || projectData?.projectName || '',
    [projectData],
  );

  const getErrorMessage = useCallback((err, fallbackMessage) => {
    const serverMessage = err?.response?.data?.message || err?.response?.data?.error;
    if (serverMessage) return serverMessage;
    const status = err?.response?.status;
    if (status === 400) return 'Missing required fields. Please provide entity_type, row_id, and employeeId.';
    if (status === 401) return 'Your session has expired. Please login again.';
    if (status === 404 || status === 441) return 'No data found for the requested update.';
    if (status === 500) return 'Something went wrong on server. Please try again.';
    return err?.message || fallbackMessage;
  }, []);
  const normalizeEffectiveDate = useCallback((value) => {
    if (!value) return '';
    const raw = String(value).trim();
    const isoCandidate = raw.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(isoCandidate)) return isoCandidate;
    return '';
  }, []);

  const openHeaderModal = useCallback((modalType, data = null) => {
    setHeaderModals((prev) => ({
      ...prev,
      [modalType]: { isOpen: true, data },
    }));
  }, []);

  const closeHeaderModal = useCallback((modalType) => {
    if (modalType === 'statusEdit') {
      statusEditRequestIdRef.current += 1;
    }
    setHeaderModals((prev) => ({
      ...prev,
      [modalType]: { isOpen: false, data: null },
    }));
  }, []);

  const openTimelineEdit = useCallback(() => {
    openHeaderModal('timelineEdit', projectData?.projectHeader?.timeline || null);
  }, [openHeaderModal, projectData]);

  const fetchStatusOptions = useCallback(async () => {
    try {
      const response = await UseAxios(getProjectStatusMaster(projectSeqId));
      const payload = response?.data;
      if (!payload || payload?.name === 'AxiosError' || payload?.isAxiosError) {
        return { options: [], current: null };
      }
      const rows = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.projectStatusList)
          ? payload.projectStatusList
          : [];
      const mappedOptions = rows
        .map((row) => ({
          value: String(row?.project_status_id ?? row?.projectStatusId ?? ''),
          label: row?.project_status_name ?? row?.projectStatusName ?? '',
        }))
        .filter((row) => row.value && row.label);
      setStatusOptions(mappedOptions);
      return {
        options: mappedOptions,
        current: payload?.current || payload?.projectStatus || null,
      };
    } catch (err) {
      setStatusOptions([]);
      snackbarOpen(getErrorMessage(err, 'Failed to fetch project status options.'), 'error');
      return { options: [], current: null };
    }
  }, [getErrorMessage, projectSeqId, snackbarOpen]);

  const checkOpenRequestAvailability = useCallback(async () => {
    if (!projectId || !employeeId) {
      return false;
    }

    const response = await UseAxios(listStatusChangeRequestHistory({
      project_id: projectId,
      project_seq_id: projectSeqId || String(projectId),
      project_name: projectName,
      logged_in_emp_id: employeeId,
      assigned_to_me: false,
      request_status: [0],
      searchQuery: '',
      selectedViewId: null,
      isTreeTable: false,
      pagination: {
        page: 1,
        limit: 10,
      },
      filter: [],
      userInfo: {
        employeeId,
        employeeNo,
      },
    }));
    const data = response?.data?.data ?? response?.data ?? null;
    const openRows = Array.isArray(data?.tableData) ? data.tableData : [];
    return openRows.some((row) => Number(row?.isApproved ?? row?.requestStatusValue) === 0);
  }, [employeeId, employeeNo, projectId, projectName, projectSeqId]);

  const openStatusEdit = useCallback(async () => {
    const requestId = statusEditRequestIdRef.current + 1;
    statusEditRequestIdRef.current = requestId;

    openHeaderModal('statusEdit', {
      project_status_id: String(projectData?.project_status_id ?? projectData?.projectStatusId ?? ''),
      remarks: '',
      effectiveDate: '',
      isBlocked: false,
      blockedMessage: '',
      isLoading: true,
    });

    const loadStatusOptions = statusOptions.length
      ? Promise.resolve({ options: statusOptions, current: null })
      : fetchStatusOptions();

    const [{ current }, hasOpenRequest] = await Promise.all([
      loadStatusOptions,
      checkOpenRequestAvailability().catch(() => false),
    ]);

    if (statusEditRequestIdRef.current !== requestId) {
      return;
    }

    setHeaderModals((prev) => ({
      ...prev,
      statusEdit: {
        isOpen: true,
        data: {
          ...(prev.statusEdit?.data || {}),
          project_status_id: String(
            current?.project_status_id
            ?? current?.projectStatusId
            ?? projectData?.project_status_id
            ?? projectData?.projectStatusId
            ?? '',
          ),
          remarks: current?.remarks ?? prev.statusEdit?.data?.remarks ?? '',
          effectiveDate: normalizeEffectiveDate(
            current?.effective_date
            || current?.status_updated_on
            || current?.effectiveDate
            || prev.statusEdit?.data?.effectiveDate,
          ),
          isBlocked: hasOpenRequest,
          blockedMessage: hasOpenRequest ? OPEN_REQUEST_BLOCK_MESSAGE : '',
          isLoading: false,
        },
      },
    }));
  }, [
    checkOpenRequestAvailability,
    fetchStatusOptions,
    normalizeEffectiveDate,
    openHeaderModal,
    projectData?.projectStatusId,
    projectData?.project_status_id,
    statusOptions,
  ]);

  const openProgressSummaryEdit = useCallback(() => {
    const latestProgress = projectData?.progressData?.[0] || null;
    openHeaderModal('progressSummaryEdit', latestProgress);
  }, [openHeaderModal, projectData]);

  const submitStatusEdit = useCallback(async (formData) => {
    try {
      if (headerModals.statusEdit?.data?.isBlocked) {
        throw new Error(headerModals.statusEdit.data.blockedMessage || OPEN_REQUEST_BLOCK_MESSAGE);
      }
      const normalizedStatusId = Number(formData?.project_status_id);
      if (!Number.isFinite(normalizedStatusId) || normalizedStatusId <= 0) {
        throw new Error('Please select a valid project status.');
      }
      const normalizedEffectiveDate = normalizeEffectiveDate(formData?.effectiveDate);
      if (!normalizedEffectiveDate) {
        throw new Error('Please select an effective date.');
      }
      const selectedStatus = statusOptions.find(
        (option) => Number(option?.value) === normalizedStatusId,
      );
      const nextStatusLabel = selectedStatus?.label || '';
      if (!projectData?.project_id) {
        throw new Error('Project id is missing for status change request.');
      }
      if (!nextStatusLabel) {
        throw new Error('Unable to resolve selected project status.');
      }
      setSubmitting((prev) => ({ ...prev, statusEdit: true }));
      const requestPayload = {
        project_id: projectData.project_id,
        new_status: nextStatusLabel,
        remarks: formData?.remarks || '',
        effective_date: normalizedEffectiveDate,
        logged_in_emp_id: employeeId,
        requested_by: employeeNo || employeeId,
      };
      const response = await UseAxios(createStatusChangeRequest(requestPayload));
      const responseData = response?.data;
      if (!responseData || responseData?.name === 'AxiosError' || responseData?.isAxiosError) {
        throw responseData || new Error('Failed to raise status change request.');
      }
      if (typeof responseData === 'object' && responseData !== null && responseData.success === false) {
        throw new Error(responseData.message || 'Failed to raise status change request.');
      }
      snackbarOpen('Status change request raised successfully.', 'success');
      closeHeaderModal('statusEdit');
    } catch (err) {
      snackbarOpen(getErrorMessage(err, 'Failed to raise status change request.'), 'error');
    } finally {
      setSubmitting((prev) => ({ ...prev, statusEdit: false }));
    }
  }, [
    closeHeaderModal,
    employeeId,
    employeeNo,
    getErrorMessage,
    headerModals.statusEdit?.data,
    normalizeEffectiveDate,
    projectId,
    snackbarOpen,
    statusOptions,
  ]);

  const submitTimelineEdit = useCallback(async (formData) => {
    if (!canEditTimeline) {
      snackbarOpen('You do not have permission to edit timeline.', 'error');
      return;
    }
    try {
      const requestedFieldValue = TIMELINE_FIELD_DISPLAY_MAP[formData?.dateType] || formData?.dateType;
      const approvalLinksList = Array.isArray(formData?.approvalLinks)
        ? formData.approvalLinks.filter(Boolean)
        : (formData?.approvalLink ? [formData.approvalLink] : []);
      const approvalLink = approvalLinksList.join(', ');

      if (['Baseline Start Date', 'Baseline End Date'].includes(requestedFieldValue) && !approvalLink) {
        snackbarOpen('Approval link is required for Baseline dates.', 'error');
        return;
      }

      if (!formData?.newDate) {
        snackbarOpen('Please select a new date.', 'error');
        return;
      }

      if (!formData?.reason?.trim()) {
        snackbarOpen('Reason for change is required.', 'error');
        return;
      }

      if (!projectId) {
        snackbarOpen('Project id is missing for timeline change request.', 'error');
        return;
      }

      setSubmitting((prev) => ({ ...prev, timelineEdit: true }));

      const resolvedVertical = projectData?.vertical_name
        || projectData?.project_vertical
        || projectData?.projectHeader?.basicDetails?.vertical
        || '';

      const payload = {
        projectId: Number(projectId),
        projectName,
        projectVertical: resolvedVertical,
        requestedFieldValue,
        newDate: formData?.newDate,
        reasonForChange: formData?.reason || '',
        approvalLink,
        logged_in_emp_id: employeeId,
        employeeId,
        employeeNo,
        payload: {
          userInfo: {
            employeeId,
            employeeNo,
            emailId: userProfile?.email || userProfile?.emailId || '',
            name: userProfile?.name || '',
            permissions: userProfile?.permissions,
          },
        },
      };

      const response = await UseAxios(createTimelineRequest(payload));
      const responsePayload = response?.data;
      if (!responsePayload || responsePayload?.name === 'AxiosError' || responsePayload?.isAxiosError) {
        throw responsePayload || new Error('Failed to raise timeline change request.');
      }
      if (typeof responsePayload === 'object' && responsePayload !== null && responsePayload.success === false) {
        throw new Error(responsePayload.message || 'Failed to raise timeline change request.');
      }

      snackbarOpen('Timeline change request raised successfully.', 'success');
      closeHeaderModal('timelineEdit');
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      snackbarOpen(getErrorMessage(err, 'Failed to raise timeline change request.'), 'error');
    } finally {
      setSubmitting((prev) => ({ ...prev, timelineEdit: false }));
    }
  }, [
    canEditTimeline,
    closeHeaderModal,
    employeeId,
    employeeNo,
    getErrorMessage,
    onRefreshData,
    projectData,
    projectId,
    projectName,
    snackbarOpen,
    userProfile,
  ]);

  const submitProgressSummaryEdit = useCallback(async (formData) => {
    setSubmitting((prev) => ({ ...prev, progressSummaryEdit: true }));
    try {
      const progressRowId = formData?.row_id ?? formData?.id;
      const progressPayload = {
        project_id: projectData?.project_id,
        date: formData?.date || null,
        progress_percent: formData?.progress,
        remarks: formData?.remarks || '',
      };
      if (!progressPayload.project_id && !progressRowId) {
        throw new Error('Project id is missing for progress update.');
      }
      const response = progressRowId
        ? await UseAxios(updateProjectProgress(progressRowId, progressPayload, accessToken))
        : await UseAxios(createProjectProgress(progressPayload, accessToken));
      const responseData = response?.data;
      if (!responseData || responseData?.name === 'AxiosError' || responseData?.isAxiosError) {
        throw responseData || new Error('Failed to update project progress.');
      }
      if (typeof responseData === 'object' && responseData !== null && responseData.success === false) {
        throw new Error(responseData.message || 'Failed to update project progress.');
      }
      snackbarOpen(progressRowId ? 'Progress updated successfully.' : 'Progress added successfully.', 'success');
      if (typeof onRefreshData === 'function') onRefreshData();
      closeHeaderModal('progressSummaryEdit');
    } catch (err) {
      snackbarOpen(getErrorMessage(err, 'Failed to update project progress.'), 'error');
    } finally {
      setSubmitting((prev) => ({ ...prev, progressSummaryEdit: false }));
    }
  }, [closeHeaderModal, getErrorMessage, onRefreshData, projectData?.project_id, snackbarOpen]);

  const handleTimelineHistory = useCallback(() => {
    const resolvedProjectId = projectData?.project_id ?? projectData?.projectId ?? null;
    const resolvedProjectSeqId = projectSeqId || resolvedProjectId || null;
    const resolvedProjectName = projectData?.project_name ?? projectData?.projectName ?? '';

    const params = new URLSearchParams();
    if (resolvedProjectId) params.set('projectId', String(resolvedProjectId));

    // Do not include projectName in the URL. projectName is passed via navigation state instead.
    closeHeaderModal('timelineEdit');
    navigate(
      `/projects/change-request/timeline${params.toString() ? `?${params.toString()}` : ''}`,
      {
        state: {
          projectId: resolvedProjectId ? String(resolvedProjectId) : undefined,
          projectSeqId: resolvedProjectSeqId ? String(resolvedProjectSeqId) : undefined,
          projectName: resolvedProjectName || undefined,
          timelineEntryMode: 'fresh',
        },
      },
    );
  }, [closeHeaderModal, navigate, projectData, projectSeqId]);

  const handleStatusHistory = useCallback(() => {
    const resolvedProjectId = projectData?.project_id ?? projectData?.projectId ?? null;
    const resolvedProjectSeqId = projectSeqId || resolvedProjectId || null;
    const resolvedProjectName = projectData?.project_name ?? projectData?.projectName ?? '';

    const params = new URLSearchParams();
    if (resolvedProjectId) params.set('projectId', String(resolvedProjectId));

    closeHeaderModal('statusEdit');
    navigate(
      `/delivery/project-details/request-history${params.toString() ? `?${params.toString()}` : ''}`,
      {
        state: {
          projectId: resolvedProjectId ? String(resolvedProjectId) : undefined,
          projectSeqId: resolvedProjectSeqId ? String(resolvedProjectSeqId) : undefined,
          projectName: resolvedProjectName || undefined,
          requestHistoryEntryMode: 'fresh',
        },
      },
    );
  }, [closeHeaderModal, navigate, projectData, projectSeqId]);

  const handleProgressHistory = useCallback(() => {
    // eslint-disable-next-line no-console
    console.log('Open progress history');
  }, []);

  useEffect(() => {
    setStatusOptions([]);
  }, [projectSeqId]);

  return {
    headerModals,
    statusOptions,
    closeHeaderModal,
    openStatusEdit,
    openTimelineEdit,
    openProgressSummaryEdit,
    submitStatusEdit,
    submitTimelineEdit,
    submitProgressSummaryEdit,
    handleStatusHistory,
    handleTimelineHistory,
    handleProgressHistory,
    submitting,
  };
};

export default useHeaderModals;
